<script lang="ts">
	import { rankItem } from '@tanstack/match-sorter-utils'
	import { differenceInDays } from 'date-fns'
	import Search from 'lucide-svelte/icons/search'
	import { ssp, queryParameters } from 'sveltekit-search-params'

	import { goto } from '$app/navigation'
	import { base } from '$app/paths'
	import Breadcrumbs from '$lib/components/breadcrumbs.svelte'
	import CardWithChart from '$lib/components/card-with-chart.svelte'
	import ChainSocial from '$lib/components/chain-social.svelte'
	import ComboBox from '$lib/components/combo-box.svelte'
	import MetricRangesFilter from '$lib/components/metric-ranges-filter.svelte'
	import ShareButton from '$lib/components/share-button.svelte'
	import { badgeVariants } from '$lib/components/ui/badge'
	import Badge from '$lib/components/ui/badge/badge.svelte'
	import { Button } from '$lib/components/ui/button'
	import * as Card from '$lib/components/ui/card'
	import { Input } from '$lib/components/ui/input'
	import * as Tooltip from '$lib/components/ui/tooltip'
	import {
		AMOUNT_OF_DAYS_FOR_NEW_LABEL,
		CHAINS_CONFIG,
		CHAINS_LIST,
		LISTED_CHAINS,
	} from '$lib/constants/chainsConfig'
	import { CHAINS_SOCIALS } from '$lib/constants/chainsSocials'
	import type { ChainId } from '$lib/models'
	import * as m from '$lib/paraglide/messages'
	import { getListedDialogStore, metricDialogStore } from '$lib/stores/dialogs'
	import { LAUNCHED_STATES, type LaunchedState } from '$lib/tanstack-table/launched-state-feature'
	import { MAX_TPS_STATES, type MaxTpsState } from '$lib/tanstack-table/max-tps-state-feature'
	import { getSocials, getChainCompetitors } from '$lib/utils/chainsHelpers'
	import { getOrdinalNumber } from '$lib/utils/i18nHelpers'
	import { phg } from '$lib/utils/posthog'
	import {
		prettifyTps,
		prettifyTime,
		prettifyType,
		prettifyTxns,
		prettifyLaunchDate,
		prettifyGovernance,
	} from '$lib/utils/prettifyMetrics'
	import { cn } from '$lib/utils/shadcn'
	import {
		getMaxTpsTooltipContent,
		getTotalTxnsTooltipContent,
		getTpsTooltipContent,
		isChainWithSpecialOneBlockMaxTps,
	} from '$lib/utils/tooltipHelpers'

	import type { PageData } from './$types'
	import MetricCard from './components/metric-card.svelte'

	export let data: PageData

	let selectedForComparison: ChainId | null = null

	const maxTpsQuery = queryParameters({ 'max-tps': ssp.string() })
	const launchedQuery = queryParameters({ launched: ssp.string() })

	let launchedState: LaunchedState =
		$launchedQuery.launched && LAUNCHED_STATES.includes($launchedQuery.launched as LaunchedState)
			? ($launchedQuery.launched as LaunchedState)
			: 'date'

	let maxTpsState: MaxTpsState =
		$maxTpsQuery['max-tps'] && MAX_TPS_STATES.includes($maxTpsQuery['max-tps'] as MaxTpsState)
			? ($maxTpsQuery['max-tps'] as MaxTpsState)
			: 'hundred-blocks'

	let isLoading = false

	let search = ''

	$: chainSocials = CHAINS_SOCIALS[data.chain.id]

	$: competitors = getChainCompetitors(data.chain.id)

	$: chainConfig = CHAINS_CONFIG[data.chain.id]

	const chains = LISTED_CHAINS.filter(listedChain => listedChain.id !== data.chain.id).map(
		({ name, id, slug }) => ({
			label: name,
			value: id,
			slug,
			disabled: false,
		})
	)
</script>

<div class="border-border/50 mg:grid-cols-[220px_auto] grid grid-cols-1 border-b">
	<nav
		class="mg:h-screen mg:min-h-full mg:w-[220px] mg:border-b-0 mg:border-r relative flex w-full flex-col border-b"
		aria-label="chains"
	>
		<div class="bg-background pt-2 pr-2 pl-2 md:pt-3 md:pr-3 md:pl-3">
			<div class="relative w-full">
				<Search class="text-muted-foreground absolute top-3 left-2.5 h-4 w-4" size="16" />

				<Input
					name="search"
					class="w-full pl-8 text-base"
					autocomplete="off"
					disabled={isLoading}
					placeholder={m.chain_page_search_placeholder()}
					size={1}
					spellcheck={false}
					type="search"
					bind:value={search}
				/>
			</div>
		</div>

		<ul
			class="mg:flex-col mg:gap-2 mg:overflow-y-auto mg:overflow-x-hidden mt-2 flex gap-1 overflow-x-auto pr-2 pb-2 pl-2 md:pr-3 md:pb-3 md:pl-3"
		>
			{#each CHAINS_LIST.filter(chainItem => rankItem(chainItem.name, search).passed) as chainItem (chainItem.id)}
				<li class="mg:min-w-[unset] relative min-w-fit">
					<Button
						class={cn('mg:min-w-full mg:gap-1.5 min-w-fit justify-start gap-1', {
							'bg-muted hover:bg-muted text-foreground hover:text-foreground':
								chainItem.slug === data.slug,
						})}
						data-active-chain={chainItem.slug === data.slug}
						data-sveltekit-reload
						href="/chain/{chainItem.slug}"
						size="sm"
						variant={chainItem.slug === data.slug ? 'default' : 'ghost'}
					>
						<img
							class="mg:size-[24px] size-[20px]"
							alt="{chainItem.name} logo"
							crossorigin="anonymous"
							decoding="async"
							fetchpriority="low"
							height="24"
							loading="lazy"
							src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{chainItem.slug}.svg"
							width="24"
						/>

						<span class="block truncate">
							{chainItem.name}
						</span>

						{#if chainItem.soon}
							<Badge class="font-bold uppercase" size="3xs" variant="secondary">
								{m.chain_page_soon_badge_text()}
							</Badge>
						{:else if chainItem.listed ? differenceInDays(new Date(), chainItem.listed) < AMOUNT_OF_DAYS_FOR_NEW_LABEL : false}
							<Badge class="font-bold uppercase" size="3xs" variant="brand">
								{m.new_badge_text()}
							</Badge>
						{/if}
					</Button>
				</li>
			{:else}
				<li class="relative min-w-fit mg:min-w-[unset] h-9 px-3 flex items-center justify-start">
					<span class="text-sm">
						{m.no_such_chain()}
					</span>
				</li>
			{/each}
		</ul>
	</nav>

	<main class="wrapper wrapper-wide relative px-3 py-4 sm:p-6">
		<Breadcrumbs
			links={[{ href: '/dashboard', text: m.link_dashboard() }, { text: data.chain.name }]}
		/>

		<section class="relative flex flex-col items-stretch gap-5 sm:gap-7">
			{#if data.chain?.soon}
				<div
					class="bg-background/10 absolute top-[50px] right-[-2px] bottom-[-2px] left-[-2px] z-1 rounded-lg backdrop-blur-xs"
				>
					<div class="sticky top-0 flex items-center justify-center pt-40 pb-2">
						<Button
							size="lg"
							variant="default"
							on:click={() => {
								getListedDialogStore.set({ open: true, chainName: data.chain.name, chainSocials })

								phg.capture('get_listed_dialog_open', {
									place: `${data.chain.name} page`,
								})
							}}
						>
							{m.get_listed_cta()}
						</Button>
					</div>
				</div>
			{/if}

			<div class="flex flex-wrap items-end justify-between gap-2">
				<div class="flex items-center justify-start gap-1.5">
					<div class="relative flex items-center justify-start gap-1.5">
						<img
							class="xs:size-[48px] size-[38px]"
							alt="{data.chain.name} logo"
							crossorigin="anonymous"
							decoding="async"
							fetchpriority="low"
							height="48"
							loading="lazy"
							src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{data.chain.slug}.svg"
							width="48"
						/>

						<h1 class="truncate text-2xl font-extrabold md:text-4xl lg:text-5xl">
							{data.chain.name}
						</h1>

						{#if data.chain.soon}
							<Badge
								class="absolute -top-2 right-3 translate-x-full font-bold uppercase"
								size="3xs"
								variant="secondary"
							>
								{m.chain_page_soon_badge_text()}
							</Badge>
						{:else if data.chain.listed ? differenceInDays(new Date(), data.chain.listed) < AMOUNT_OF_DAYS_FOR_NEW_LABEL : false}
							<Badge
								class="absolute -top-2 right-3 translate-x-full font-bold uppercase"
								size="3xs"
								variant="brand"
							>
								{m.new_badge_text()}
							</Badge>
						{/if}
					</div>

					{#if !data.chain.soon && data.chain.index}
						<Tooltip.Root>
							<Tooltip.Trigger class={cn(badgeVariants({ size: 'sm', variant: 'secondary' }))}>
								#{data.chain.index}
							</Tooltip.Trigger>

							<Tooltip.Content>
								<p>
									{m.chain_page_rank_tooltip_text({
										rank: getOrdinalNumber(data.chain.index),
										all: LISTED_CHAINS.length,
									})}
								</p>
							</Tooltip.Content>
						</Tooltip.Root>
					{/if}
				</div>

				<div class="flex items-center">
					{#if !data.chain?.soon}
						<MetricRangesFilter class="mr-2" place="chain_page_controls" bind:isLoading />
					{/if}

					<ShareButton urlToCopy="https://chainspect.app/chain/{data.chain.slug}" />
				</div>
			</div>

			<div class="mg:grid-cols-4 mg:gap-4 grid w-full grid-cols-2 gap-2">
				<MetricCard
					name={m.metric_tps_name()}
					change={data.chain.tps?.change}
					{isLoading}
					onHintClick={() => {
						metricDialogStore.set({
							open: true,
							type: 'tps',
						})
					}}
					popoverAriaLabel={m.tooltip_tps_label({ chain: data.chain.name })}
					popoverContent={getTpsTooltipContent(data.chain)}
					value={prettifyTps(data.chain.tps?.value)}
				/>

				<MetricCard
					name={maxTpsState === 'hundred-blocks'
						? m.metric_max_tps_hundred_blocks_name()
						: m.metric_max_tps_one_block_name()}
					actionState={maxTpsState === 'hundred-blocks' ? false : true}
					actionText={maxTpsState === 'hundred-blocks'
						? m.metric_max_tps_switch_to_one_block()
						: m.metric_max_tps_switch_to_hundred_blocks()}
					{isLoading}
					onActionClick={() => {
						maxTpsState = maxTpsState === 'hundred-blocks' ? 'one-block' : 'hundred-blocks'

						if (MAX_TPS_STATES.includes(maxTpsState)) {
							maxTpsQuery.set({ 'max-tps': maxTpsState })
						}

						phg.capture('max_tps_column_state_change', {
							new_state: maxTpsState,
						})
					}}
					onHintClick={() => {
						metricDialogStore.set({
							open: true,
							type: 'max-tps',
						})
					}}
					popoverAriaLabel={maxTpsState === 'hundred-blocks'
						? m.tooltip_max_tps_hundred_blocks_label({ chain: data.chain.name })
						: m.tooltip_max_tps_one_block_label({ chain: data.chain.name })}
					popoverContent={getMaxTpsTooltipContent(data.chain, maxTpsState)}
					popoverType={maxTpsState === 'one-block' &&
					isChainWithSpecialOneBlockMaxTps(data.chain.id)
						? 'warning'
						: 'info'}
					value={prettifyTps(
						maxTpsState === 'hundred-blocks'
							? data.chain.maxTps?.value
							: data.chain.maxTpsOneBlock?.value
					)}
				/>

				<MetricCard
					name={m.metric_theor_tps_name()}
					{isLoading}
					onHintClick={() => {
						metricDialogStore.set({
							open: true,
							type: 'theor-tps',
						})
					}}
					value={prettifyTps(data.chain.theorTps)}
				/>

				<MetricCard
					name={m.metric_total_transactions_name()}
					{isLoading}
					isNew
					onHintClick={() => {
						metricDialogStore.set({
							open: true,
							type: 'txns',
						})
					}}
					popoverAriaLabel={m.tooltip_total_txns_label({ chain: data.chain.name })}
					popoverContent={getTotalTxnsTooltipContent(data.chain)}
					value={prettifyTxns(data.chain.txns)}
				/>

				<MetricCard
					name={m.metric_blocktime_name()}
					{isLoading}
					onHintClick={() => {
						metricDialogStore.set({
							open: true,
							type: 'blocktime',
						})
					}}
					popoverAriaLabel={m.tooltip_blocktime_label({ chain: data.chain.name })}
					popoverContent={data.chain.id !== 'hedera_chain'
						? null
						: m.hedera_chain_blocktime_explanation()}
					value={prettifyTime(data.chain.blocktime)}
				/>

				<MetricCard
					name={m.metric_ttf_name()}
					{isLoading}
					onHintClick={() => {
						metricDialogStore.set({
							open: true,
							type: 'ttf',
						})
					}}
					popoverAriaLabel={m.tooltip_ttf_label({ chain: data.chain.name })}
					popoverContent={data.chain.id !== 'flow_chain' ? null : m.flow_chain_ttf_explanation()}
					value={prettifyTime(data.chain.ttf)}
				/>

				<MetricCard name={m.metric_nc_name()} {isLoading} value={m.metrics_soon()} />

				<MetricCard name={m.metric_active_addresses_name()} {isLoading} value={m.metrics_soon()} />

				<MetricCard
					name={m.metric_governance_name()}
					{isLoading}
					onHintClick={() => {
						metricDialogStore.set({
							open: true,
							type: 'governance',
						})
					}}
					popoverAriaLabel={m.tooltip_governance_label({ chain: data.chain.name })}
					popoverContent={data.chain.governance
						? m[`tooltip_governance_${data.chain.governance}`]()
						: null}
					value={prettifyGovernance(data.chain.governance)}
				/>

				<MetricCard
					name={m.metric_type_name()}
					{isLoading}
					value={prettifyType(data.chain.type, false)}
				/>

				<MetricCard
					name={launchedState === 'date' ? m.metric_launch_date_name() : m.metric_launch_age_name()}
					actionState={launchedState === 'date' ? false : true}
					actionText={launchedState === 'date'
						? m.metric_launch_switch_to_age()
						: m.metric_launch_switch_to_date()}
					{isLoading}
					onActionClick={() => {
						launchedState = launchedState === 'date' ? 'age' : 'date'

						if (LAUNCHED_STATES.includes(launchedState)) {
							launchedQuery.set({ launched: launchedState })
						}

						phg.capture('launched_column_state_change', {
							new_state: launchedState,
						})
					}}
					onHintClick={() => {
						metricDialogStore.set({
							open: true,
							type: 'launch-date',
						})
					}}
					popoverAriaLabel={launchedState == 'date'
						? m.tooltip_launch_date_label({ chain: data.chain.name })
						: m.tooltip_launch_age_label({ chain: data.chain.name })}
					popoverContent={data.chain.id !== 'ecash_chain'
						? null
						: m.ecash_chain_launch_date_explanation()}
					value={prettifyLaunchDate(data.chain.launched, launchedState)}
				/>
			</div>

			{#if !data.chain.soon}
				<CardWithChart
					chainIds={[data.chain.id]}
					chartData={data.chartData}
					title={m.chain_page_chart_block_title({ chain: data.chain.name })}
					titleTag="h2"
				/>
			{/if}

			<Card.Root>
				<Card.Header>
					<Card.Title tag="h2">
						{m.chain_page_compare_block_title({ chain: data.chain.name })}
					</Card.Title>
				</Card.Header>

				<Card.Content>
					<div class="flex w-full flex-col items-stretch justify-center gap-3 md:flex-row">
						<ComboBox
							class="w-full"
							noResults={m.no_such_chain()}
							options={chains}
							placeholder={m.chain_page_compare_select_placeholder()}
							bind:value={selectedForComparison}
						/>

						<Button
							disabled={!selectedForComparison}
							variant="outline"
							on:click={() => {
								if (selectedForComparison) {
									const { slug } = CHAINS_CONFIG[selectedForComparison]

									goto(`${base}/compare/${data.chain.slug}-vs-${slug}`)
									phg.capture('compare_chains', {
										place: 'chain_page',
										leftChain: data.chain.id,
										rightChain: selectedForComparison,
									})
								}
							}}
						>
							{m.chain_page_compare_button()}
						</Button>
					</div>

					{#if Array.isArray(competitors) && competitors.length > 0}
						<h3 class="mt-6 text-base">
							{m.chain_page_compare_popular_comparisons_title({ chain: data.chain.name })}
						</h3>

						<ul
							class="links-inside xs:columns-2 xs:space-y-1 mt-3 space-y-2 self-start text-sm sm:text-base"
						>
							{#each competitors as competitor (competitor.id)}
								<li>
									<a href="{base}/compare/{data.chain.slug}-vs-{competitor.slug}">
										{m.chain_page_compare_popular_compare_vs({
											chainLeft: data.chain.name,
											chainRight: competitor.name,
										})}
									</a>
								</li>
							{/each}
						</ul>
					{/if}
				</Card.Content>
			</Card.Root>

			<Card.Root>
				<Card.Header>
					<Card.Title tag="h2">
						{m.chain_page_about_chain_title({ chain: data.chain.name })}
					</Card.Title>
				</Card.Header>

				<Card.Content class="prose">
					{#if data.chain.id !== 'flow_chain'}
						<p>{m[`${data.chain.id}_about`]()}</p>
					{:else}
						<!-- eslint-disable-next-line svelte/no-at-html-tags -->
						{@html m.flow_chain_about()}
					{/if}

					{#if chainConfig.token}
						<p>
							<!-- eslint-disable-next-line svelte/no-at-html-tags -->
							{@html m.chain_page_about_chain_token({ token: chainConfig.token })}
						</p>
					{/if}

					{#if data.chain.soon}
						<p>
							<!-- eslint-disable-next-line svelte/no-at-html-tags -->
							{@html m.chain_page_about_metrics_overview_coming_soon({
								chain: data.chain.name,
							})}
						</p>
					{:else}
						<p>
							<strong>{m.chain_page_about_metrics_overview_title()}</strong>

							<br />

							{#if data.chain.tps?.value != null}
								{m.chain_page_about_metrics_overview_tps({
									chain: data.chain.name,
									tps: prettifyTps(data.chain.tps?.value, 'long'),
								})}
							{:else}
								{m.chain_page_about_metrics_overview_tps_soon({
									chain: data.chain.name,
								})}
							{/if}

							<br />

							{#if data.chain.maxTps?.value != null}
								{m.chain_page_about_metrics_overview_max_tps({
									chain: data.chain.name,
									tps: prettifyTps(data.chain.maxTps?.value, 'long'),
								})}
							{:else}
								{m.chain_page_about_metrics_overview_max_tps_soon({
									chain: data.chain.name,
								})}
							{/if}

							<br />

							{#if data.chain.theorTps != null}
								{m.chain_page_about_metrics_overview_theor_tps({
									chain: data.chain.name,
									tps: prettifyTps(data.chain.theorTps, 'long'),
								})}
							{:else}
								{m.chain_page_about_metrics_overview_theor_tps_soon({
									chain: data.chain.name,
								})}
							{/if}

							<br />

							{#if data.chain.txns != null}
								{m.chain_page_about_metrics_overview_total_txns({
									chain: data.chain.name,
									txns: prettifyTxns(data.chain.txns, 'none'),
								})}
							{:else}
								{m.chain_page_about_metrics_overview_total_txns_soon({
									chain: data.chain.name,
								})}
							{/if}

							<br />

							{#if data.chain.blocktime != null}
								{m.chain_page_about_metrics_overview_blocktime({
									chain: data.chain.name,
									tps: prettifyTime(data.chain.blocktime),
								})}
							{:else}
								{m.chain_page_about_metrics_overview_blocktime_soon({
									chain: data.chain.name,
								})}
							{/if}

							<br />

							{#if data.chain.ttf != null}
								{m.chain_page_about_metrics_overview_ttf({
									chain: data.chain.name,
									tps: prettifyTime(data.chain.ttf),
								})}
							{:else}
								{m.chain_page_about_metrics_overview_ttf_soon({
									chain: data.chain.name,
								})}
							{/if}

							<br />

							{m.chain_page_about_metrics_overview_nc_soon({
								chain: data.chain.name,
							})}

							<br />

							{m.chain_page_about_metrics_overview_active_addresses_soon({
								chain: data.chain.name,
							})}

							<br />

							{#if data.chain.governance != null}
								{m.chain_page_about_metrics_overview_gov_type({
									chain: data.chain.name,
									governance: prettifyGovernance(data.chain.governance).toLowerCase(),
								})}
							{:else}
								{m.chain_page_about_metrics_overview_gov_type_soon({
									chain: data.chain.name,
								})}
							{/if}

							<br />

							{#if data.chain.type != null}
								{m.chain_page_about_metrics_overview_type({
									chain: data.chain.name,
									type: prettifyType(data.chain.type),
								})}
							{:else}
								{m.chain_page_about_metrics_overview_type_soon({
									chain: data.chain.name,
								})}
							{/if}

							<br />

							{#if data.chain.launched != null}
								{m.chain_page_about_metrics_overview_launched({
									chain: data.chain.name,
									launched: `${prettifyLaunchDate(data.chain.launched, 'date')} (${prettifyLaunchDate(data.chain.launched, 'age')})`,
								})}
							{:else}
								{m.chain_page_about_metrics_overview_launched_soon({
									chain: data.chain.name,
								})}
							{/if}
						</p>
					{/if}
				</Card.Content>
			</Card.Root>

			{#if data.chain.id === 'hedera_chain'}
				<Card.Root>
					<Card.Header>
						<Card.Title id="why-is-hedera-tps-so-high" tag="h2">
							{m.chain_page_why_is_hedera_tps_so_high_title()}
						</Card.Title>
					</Card.Header>

					<Card.Content class="prose">
						<p>
							<!-- eslint-disable-next-line svelte/no-at-html-tags -->
							{@html m.hedera_chain_why_tps_is_high()}
						</p>
					</Card.Content>
				</Card.Root>
			{/if}

			{#if chainSocials}
				<Card.Root>
					<Card.Header>
						<Card.Title tag="h2">
							{m.chain_page_socials_title({ chain: data.chain.name })}
						</Card.Title>
					</Card.Header>

					<Card.Content>
						<div class="flex flex-wrap items-center justify-start gap-2">
							{#each getSocials(chainSocials) as { type, link } (link)}
								<ChainSocial {link} {type} />
							{/each}
						</div>
					</Card.Content>
				</Card.Root>
			{/if}
		</section>
	</main>
</div>
